import { useRef, useEffect } from "react";
import RoundPhaseWrapper from "./roundPhaseWrapper";
import ThirdPlace from "./thirdPlace/thirdPlace";
import Final from "./final/final";
import Champion from "./champion/champion";
import WinnerDisplay from "./winnerDisplay";
import QualifyingRoundsPanelMobile from "./qualifyingRoundsPanelMobile/qualifyingRoundsPanelMobile";
import { useIsMobileBreakpoint } from "../../../hooks/useIsMobileBreakpoint";
import "./qualifyingRoundsPanel.sass";
import "./qualifyingCapture.sass";
import "swiper/css";

// Orden visual de dieciseisavos (por matchNumber) para el bracket de eliminatorias
export const R32_DISPLAY_ORDER = [74, 77, 73, 75, 84, 83, 81, 82, 76, 78, 79, 80, 88, 86, 85, 87];

// Orden visual de octavos (por matchNumber)
export const R16_DISPLAY_ORDER = [89, 90, 93, 94, 91, 92, 95, 96];

export function reorderByMatchNumber(matches, order) {
  if (!matches || matches.length === 0) return matches;
  const byMatchNum = {};
  matches.forEach(m => { byMatchNum[m.matchNumber] = m; });
  return order.map(mn => byMatchNum[mn]).filter(Boolean);
}

// Estilos inline compartidos para paneles de captura
const captureStyle = {
  position: "absolute",
  left: "-99999px",
  top: 0,
  overflow: "visible",
  pointerEvents: "none",
  zIndex: -1,
};

// Panel de captura para DESKTOP: desde octavos hasta campeón
function CapturePanelDesktop({ qualifyingState, captureRef }) {
  const winner = qualifyingState.final?.[0]?.winner;
  return (
    <div
      className="QualifyingRoundsPanel QualifyingCapture"
      ref={captureRef}
      aria-hidden="true"
      style={captureStyle}
    >
      <div className="QualifyingContainer">
        <RoundPhaseWrapper
          title="OCTAVOS"
          matches={reorderByMatchNumber(qualifyingState.roundOf16 || [], R16_DISPLAY_ORDER)}
          onSelectWinner={() => {}}
          className="RoundOf16"
          groupingStrategy="octets"
        />
        <RoundPhaseWrapper
          title="CUARTOS"
          matches={qualifyingState.roundOf8 || []}
          onSelectWinner={() => {}}
          className="RoundOf8"
          groupingStrategy="pairs"
        />
        <div className="SemiFinalsAndFinal">
          <div className="SemiFinalsAndThirdPlace">
            <RoundPhaseWrapper
              title="SEMIFINALES"
              matches={qualifyingState.semiFinals || []}
              onSelectWinner={() => {}}
              className="SemiFinals"
              groupingStrategy="none"
            />
            <ThirdPlace match={qualifyingState.thirdPlace?.[0]} onSelectWinner={() => {}} />
          </div>
          <div className="FinalAndChampion">
            <Final match={qualifyingState.final?.[0]} onSelectWinner={() => {}} />
            <div className="ChampionSection">
              <WinnerDisplay winner={winner} variant="champion" />
              <div className="ChampionTitle">CAMPEÓN</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function QualifyingRoundsPanel({ qualifyingState, onSelectWinner, scrollToChampion, onScrollComplete }) {
  const isMobile = useIsMobileBreakpoint(780);
  const containerRef = useRef(null);
  const captureRef = useRef(null);
  const championRef = useRef(null);

  // Auto-scroll al campeón en desktop cuando se simula
  useEffect(() => {
    if (scrollToChampion && !isMobile && containerRef.current) {
      // Esperar al siguiente frame para que el DOM se actualice
      requestAnimationFrame(() => {
        if (championRef.current) {
          championRef.current.scrollIntoView({ behavior: "smooth", inline: "end", block: "nearest" });
        } else {
          // Fallback: scroll al final del contenedor
          containerRef.current.scrollLeft = containerRef.current.scrollWidth;
        }
        onScrollComplete?.();
      });
    }
  }, [scrollToChampion, isMobile, onScrollComplete]);

  if (isMobile) {
    return (
      <>
        <CapturePanelDesktop qualifyingState={qualifyingState} captureRef={captureRef} />
        <QualifyingRoundsPanelMobile
          qualifyingState={qualifyingState}
          onSelectWinner={onSelectWinner}
          captureRef={captureRef}
          scrollToChampion={scrollToChampion}
          onScrollComplete={onScrollComplete}
        />
      </>
    );
  }

  return (
    <>
      <CapturePanelDesktop qualifyingState={qualifyingState} captureRef={captureRef} />
      <div className="QualifyingRoundsPanel" ref={containerRef}>
        <div className="QualifyingContainer">
          <RoundPhaseWrapper
            title="DIECISEISAVOS"
            matches={reorderByMatchNumber(qualifyingState.roundOf32 || [], R32_DISPLAY_ORDER)}
            onSelectWinner={onSelectWinner}
            className="RoundOf32"
            groupingStrategy="quads"
          />

          <RoundPhaseWrapper
            title="OCTAVOS"
            matches={reorderByMatchNumber(qualifyingState.roundOf16 || [], R16_DISPLAY_ORDER)}
            onSelectWinner={onSelectWinner}
            className="RoundOf16"
            groupingStrategy="octets"
          />

          <RoundPhaseWrapper
            title="CUARTOS"
            matches={qualifyingState.roundOf8 || []}
            onSelectWinner={onSelectWinner}
            className="RoundOf8"
            groupingStrategy="pairs"
          />

          <div className="SemiFinalsAndFinal">
            <div className="SemiFinalsAndThirdPlace">
              <RoundPhaseWrapper
                title="SEMIFINALES"
                matches={qualifyingState.semiFinals || []}
                onSelectWinner={onSelectWinner}
                className="SemiFinals"
                groupingStrategy="none"
              />

              <ThirdPlace match={qualifyingState.thirdPlace?.[0]} onSelectWinner={onSelectWinner} />
            </div>

            <div className="FinalAndChampion">
              <Final match={qualifyingState.final?.[0]} onSelectWinner={onSelectWinner} />

              <div ref={championRef}>
                <Champion winner={qualifyingState.final?.[0]?.winner} qualifyingState={qualifyingState} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}